import Line from './Line';
import {
  degreeToRadian,
  findCircleLineIntersections,
  isDefined,
} from '../utils';

export default class ArcLine extends Line {
  direction = 1;
  position = this.line.position;
  type = 'circle';
  dependentLine;
  dependentLineType;
  dependentLinePosition;
  dependentFrom;

  constructor(...props) {
    super(...props);
    this.lineData.set({
      to: 0,
      startAngle: props[1].startAngle || 0,
      radius: props[1].radius || 0,
      maxAngle: props[1].maxAngle,
      clockwise: props[1].clockwise !== undefined ? props[1].clockwise : true,
      collapse: props[1].collapse || false,
    });
  }
  toggleDirection() {
    this.direction *= -1;
  }

  toggleClockwise() {
    this.lineData.clockwise = !this.lineData.clockwise;
  }

  calcAngles(intersection, to) {
    const [{ angle, x, y }] = intersection;

    const { drawFromAngle, drawAngle, drawFull } = this.instructions;
    const oldEndAngle = drawFromAngle + to;
    let startAngle, endAngle;

    // Vertical from the right
    if (this.dependentLineType === 'vertical' && this.dependentFrom === 100) {
      startAngle =
        drawFromAngle > angle && drawAngle !== 360 ? drawFromAngle : angle;
      const newEndAngle = 360 - angle;
      endAngle = oldEndAngle > newEndAngle ? newEndAngle : oldEndAngle;

      if (!this.lineData.clockwise) {
        startAngle = -startAngle;
        endAngle = 360 - endAngle;
      }
    }
    // Vertical from the left
    else if (this.dependentLineType === 'vertical') {
      startAngle =
        drawFromAngle < angle && drawAngle !== 360
          ? drawFromAngle
          : angle + 180;
      const newEndAngle = 180 - angle;
      endAngle = oldEndAngle - 360 > newEndAngle ? newEndAngle : oldEndAngle;

      if (!this.lineData.clockwise) {
        const tmpStart = startAngle;
        startAngle =
          180 - drawFromAngle > angle && !drawFull
            ? drawFromAngle
            : 180 - angle;

        endAngle = !drawFull ? 180 - tmpStart : -newEndAngle;
      }
    }
    // Horizontal from bottom
    else if (
      this.dependentLineType === 'horizontal' &&
      this.dependentFrom === 100
    ) {
      startAngle =
        drawFromAngle - 90 > angle && drawAngle !== 360
          ? drawFromAngle
          : 90 + angle;
      const newEndAngle = 90 - angle;
      endAngle = oldEndAngle - 360 > newEndAngle ? newEndAngle : oldEndAngle;

      if (!this.lineData.clockwise) {
        const clipAngle = angle - 270;
        const currentEnd = drawFromAngle - to;
        startAngle = 180 - startAngle;
        endAngle = clipAngle < currentEnd ? currentEnd : clipAngle;
      }
    }
    // Horizontal from top
    else if (this.dependentLineType === 'horizontal') {
      startAngle =
        drawFromAngle - 270 > angle && drawAngle !== 360
          ? drawFromAngle
          : angle + 270;
      const newEndAngle = 270 - angle;
      endAngle =
        oldEndAngle - 360 > newEndAngle ? newEndAngle : oldEndAngle - 360;

      if (!this.lineData.clockwise) {
        const clipAngle = angle - 90;
        const currentEnd = drawFromAngle - to;
        startAngle = 180 - startAngle;
        endAngle = clipAngle < currentEnd ? currentEnd : clipAngle;
      }
    }

    // return { startAngle: 160, endAngle: 8 };
    return { startAngle, endAngle };
  }

  setDependentLine(line, type, height, width) {
    this.height = height;
    this.width = width;

    this.dependentLine = line;
    this.dependentLineType = type;
    this.dependentLinePosition = line.getPosition();

    if (
      (this.dependentLineType === 'vertical' &&
        this.dependentLinePosition.x > this.startPos.x) ||
      (this.dependentLineType === 'horizontal' &&
        this.dependentLinePosition.y > this.startPos.y)
    ) {
      this.dependentFrom = 100;
    } else {
      this.dependentFrom = 0;
    }

    line.setOnChange(position => {
      this.dependentLinePosition = position;
      const intersection = this.findLineIntersection(height, width);

      if (intersection.length === 0) return;

      if (!this.isDrawing) {
        this.resetLine(true);
        const { startAngle, endAngle } = this.calcAngles(
          intersection,
          this.lineData.to,
        );
        this.line.arc(
          this.startPos.x,
          this.startPos.y,
          this.lineData.radius,
          degreeToRadian(startAngle),
          degreeToRadian(endAngle),
          !this.lineData.clockwise,
        );
      }
    });
  }

  findLineIntersection(height, width) {
    let x1, y1, x2, y2;

    if (this.dependentLineType === 'horizontal') {
      x1 = this.dependentLinePosition.x;
      x2 = width;
      y1 = this.dependentLinePosition.y;
      y2 = y1;
    } else {
      x1 = this.dependentLinePosition.x;
      x2 = x1;
      y1 = this.dependentLinePosition.y;
      y2 = height;
    }

    return findCircleLineIntersections(
      this.startPos.x,
      this.startPos.y,
      this.lineData.radius,
      x1,
      y1,
      x2,
      y2,
    );
  }

  onUpdate({
    to,
    clockwise,
    endAngle: originalEndAngle,
    startAngle: originalStartAngle,
    radius,
    maxAngle: originalMaxAngle,
    collapse,
    endDraw,
  }) {
    if (!isDefined(this.line)) return;
    this.resetLine();
    let startAngle, endAngle;
    let maxStart = originalStartAngle;
    let maxAngle = originalMaxAngle;

    if (collapse) {
      const diff = endDraw.to - to;
      if (diff < maxAngle) maxAngle = diff;
    }

    if (clockwise) {
      if (this.direction === -1) {
        endAngle = originalEndAngle;
        startAngle = originalStartAngle + to;
      } else {
        if (isDefined(maxAngle) && to > maxAngle) {
          maxStart = maxStart + to - maxAngle;
        }
        startAngle = maxStart;
        endAngle = originalStartAngle + to;
        this.lineData.endAngle = endAngle;
      }
    } else {
      if (this.direction === -1) {
        endAngle = originalEndAngle;
        startAngle = originalStartAngle - to;
      } else {
        if (isDefined(maxAngle) && to > maxAngle) {
          maxStart = maxStart - to + maxAngle;
        }

        startAngle = maxStart;
        endAngle = -to + originalStartAngle;
        this.lineData.endAngle = endAngle;
      }
    }

    if (isDefined(this.dependentLine)) {
      const intersection = this.findLineIntersection(this.height, this.width);
      if (intersection.length !== 0) {
        const angles = this.calcAngles(intersection, to);
        startAngle = angles.startAngle;
        endAngle = angles.endAngle;
      }
    }

    this.line.arc(
      this.startPos.x,
      this.startPos.y,
      radius,
      degreeToRadian(startAngle),
      degreeToRadian(endAngle),
      !this.lineData.clockwise,
    );
  }
}
